import { ChartDatasetModel } from '@abb-procure/data';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { Options } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';

@Component({
  selector: 'procure-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
  imports: [HighchartsChartComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineChart {
  readonly dataset = input<ChartDatasetModel | null | undefined>();
  readonly datasets = input<ChartDatasetModel[] | null | undefined>();
  readonly type = input<'number' | 'percentage'>('number');
  readonly seriesType = input<'line' | 'area'>('line');
  readonly stackType = input<'normal' | null>(null);
  protected readonly hasData = computed(() => {
    const datasets = this.datasets();
    if (datasets?.length) {
      return datasets.some((dataset) => (dataset.data?.length ?? 0) > 0);
    }

    return (this.dataset()?.data?.length ?? 0) > 0;
  });

  // eslint-disable-next-line max-lines-per-function
  protected readonly chartOptions = computed<Options>(() => {
    const type = this.type();
    const seriesType = this.seriesType();
    const stackType = this.stackType();
    const effectiveDatasets = this.buildDatasets(
      this.datasets(),
      this.dataset(),
    );
    const allValues = this.collectValues(effectiveDatasets);
    const maxValue = this.calculateMaxValue({
      datasets: effectiveDatasets,
      type,
      stackType,
      allValues,
    });

    return {
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      chart: {
        type: seriesType,
        height: 250,
      },
      legend: {
        enabled: effectiveDatasets.length > 1,
      },
      plotOptions: {
        series: {
          stacking: stackType ?? undefined,
        },
      },
      series: effectiveDatasets.map((ds) => ({
        type: seriesType,
        name: ds.label ?? '',
        data: ds.data?.map((data) => data.value),
        lineWidth: 2,
        color: ds.color ?? undefined,
        fillOpacity: seriesType === 'area' ? 0.25 : undefined,
      })),
      xAxis: {
        title: {
          text: '',
        },
        categories: effectiveDatasets[0]?.data?.map((data) => data.name ?? ''),
      },
      yAxis: {
        title: {
          text: '',
        },
        labels: {
          format: this.type() === 'percentage' ? '{value} %' : '{value}',
        },
        min: 0,
        max: maxValue,
        tickPixelInterval: 50,
        minTickInterval: 1,
        gridLineWidth: 1,
      },
      tooltip: {
        formatter(): string {
          return `<b>${this.key}</b><br/><span style="color: ${this.series.color}">\u25CF</span>
           ${this.series.name}: ${this.y}${type === 'percentage' ? ' %' : ''}`;
        },
      },
    };
  });

  private buildDatasets(
    datasetsInput: ChartDatasetModel[] | null | undefined,
    datasetInput: ChartDatasetModel | null | undefined,
  ): ChartDatasetModel[] {
    const result: ChartDatasetModel[] = [];

    if (datasetsInput?.length) {
      result.push(...datasetsInput);
    } else if (datasetInput) {
      result.push(datasetInput);
    }

    return result;
  }

  private collectValues(datasets: ChartDatasetModel[]): number[] {
    return datasets.flatMap((ds) => ds.data?.map((item) => item.value) ?? []);
  }

  private calculateMaxValue(params: {
    datasets: ChartDatasetModel[];
    type: 'number' | 'percentage';
    stackType: 'normal' | null;
    allValues: number[];
  }): number {
    const { datasets, type, stackType, allValues } = params;

    if (type === 'percentage') {
      return 100;
    }

    if (!allValues.length) {
      return 0;
    }

    if (stackType === 'normal' && datasets.length > 1) {
      return this.computeStackedMax(datasets);
    }

    return Math.max(...allValues);
  }

  private computeStackedMax(datasets: ChartDatasetModel[]): number {
    const pointCount = Math.max(
      ...datasets.map((dataset) => dataset.data?.length ?? 0),
      0,
    );

    const sums = new Array(pointCount).fill(0);

    for (const dataset of datasets) {
      for (let pointIndex = 0; pointIndex < pointCount; pointIndex += 1) {
        sums[pointIndex] += dataset.data?.[pointIndex]?.value ?? 0;
      }
    }

    return sums.length ? Math.max(...sums) : 0;
  }
}
